import React from 'react';
import { TrendingDown } from 'lucide-react';
import OptimizedImage from '../OptimizedImage';
import { formatPrice, getBookingComPrice, getSavingsVsBcom } from './pricing';

// Reusable booking summary modal, shared by Rooms (grid) and RoomDetail (sidebar
// CTA). Renders nothing when `open` is false.
export const BookingSummaryModal = ({
  open,
  onClose,
  onConfirm,
  room,           // { name, images, price, pricing? }
  bookingSummary, // { checkIn, checkOut, adults, children, numNights }
  title = 'Booking Summary',
  confirmLabel = 'Complete Booking →',
  confirmTestId = 'complete-booking-btn',
}) => {
  if (!open || !room) return null;

  const { checkIn, checkOut, adults = 0, children = 0, numNights } = bookingSummary || {};
  const pricing = room.pricing;
  const nightsLabel = numNights || 1;

  const dateLabel = checkIn && checkOut && (
    <>
      <p className="text-gray-600 text-sm mt-1">
        {new Date(checkIn).toLocaleDateString('en', { month: 'short', day: 'numeric' })} - {new Date(checkOut).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
      </p>
      <p className="text-gray-500 text-sm">{adults} adult{adults !== 1 ? 's' : ''}{children > 0 ? `, ${children} child${children > 1 ? 'ren' : ''}` : ''}</p>
      <p className="text-gray-500 text-sm">{nightsLabel} night{nightsLabel !== 1 ? 's' : ''}</p>
    </>
  );

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="La Rosa Negra" className="w-10 h-10 rounded-lg object-cover shadow" />
            <div>
              <h3 className="font-bold">Hotel La Rosa Negra</h3>
              <p className="text-sm text-gray-300">{title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/10 p-2 rounded-full transition-colors text-xl"
            data-testid="booking-modal-close"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <OptimizedImage src={room.images?.[0]} alt={room.name} className="w-24 h-24 rounded-xl object-cover shadow-md" />
            <div>
              <h4 className="font-bold text-gray-900 text-lg">{room.name}</h4>
              {dateLabel || (
                <p className="text-gray-500 text-sm mt-1">Select dates above to see pricing</p>
              )}
            </div>
          </div>

          {/* Price summary */}
          {pricing ? (
            <div className="bg-orange-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Price per night (avg)</span>
                <span className="font-semibold">{formatPrice(pricing.pricing.price_per_night)}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">{nightsLabel} nights</span>
                <span className="font-semibold">×{nightsLabel}</span>
              </div>
              <div className="border-t border-orange-200 mt-2 pt-2 flex justify-between items-center">
                <span className="font-bold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-orange-600">{formatPrice(pricing.pricing.total_price)}</span>
              </div>
              {getSavingsVsBcom(pricing.pricing.total_price) > 0 && (
                <div className="mt-3 pt-3 border-t border-orange-200 flex items-center justify-between text-sm" data-testid="modal-bcom-comparison">
                  <span className="text-gray-500 line-through">
                    {formatPrice(getBookingComPrice(pricing.pricing.total_price))} on Booking.com
                  </span>
                  <span className="text-emerald-600 font-semibold flex items-center gap-1">
                    <TrendingDown size={14} />
                    You save {formatPrice(getSavingsVsBcom(pricing.pricing.total_price))}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Starting from</span>
                <span className="text-xl font-bold text-orange-600">from €{room.price}/night</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">Select dates above to see exact pricing</p>
            </div>
          )}

          <button
            onClick={onConfirm}
            className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-xl font-semibold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg text-center"
            data-testid={confirmTestId}
          >
            {confirmLabel}
          </button>

          <p className="text-center text-xs text-gray-500 mt-3">Continue to our secure booking page</p>

          <div className="mt-4 pt-4 border-t flex justify-center gap-6 text-xs text-gray-500">
            <span>🔒 Secure</span>
            <span>✓ Free Cancellation</span>
            <span>🏆 Best Price</span>
          </div>
        </div>
      </div>
    </div>
  );
};
